import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Mail, Phone, MessageSquare, Instagram, PhoneIcon } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { motion } from "framer-motion";
import logowhite from "../../assets/TWH1-white.png";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { indexStore } from "../../store/indexStore";
import i18n from "../../i18n";
function FooterPages() {
  const { t } = useTranslation();
  const { lang, changeLang } = indexStore();

  const handelSelectChange = (e: any) => {
    changeLang(e);
  };
  useEffect(() => {
    const handleLanguageChange = () => {
      i18n.changeLanguage(lang);
      document.documentElement.dir = i18n.dir();
    };

    if (lang) {
      handleLanguageChange();
    } else {
      changeLang(localStorage.getItem("Lang") || "en");
    }
  }, [lang]);
  return (
    <footer className="bg-gradient-to-br from-MainBlue via-MainBlue to-blue-600 text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 lg:gap-12">
          {/* About Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="space-y-4"
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center gap-2 mb-4"
            >
              <img
                src={logowhite}
                alt="TWH Menu Logo"
                className="h-10 md:h-12 w-auto object-contain"
              />
            </motion.div>
            <p className="text-sm md:text-base leading-relaxed text-white/90">
              {t("footer.footerDesc")}
            </p>
            <p className="text-xs md:text-sm mt-6 text-white/70">
              {t("footer.allRightsReserved")} &copy; {new Date().getFullYear()}
            </p>
          </motion.div>

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="space-y-4"
          >
            <h3 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">
              {t("footer.contactUs")}
            </h3>
            <ul className="space-y-3 md:space-y-4">
              <motion.li
                whileHover={{ x: 5 }}
                className="flex items-center gap-3 group"
              >
                <div className="p-2 rounded-lg bg-white/10 group-hover:bg-white/20 transition-colors">
                  <Phone size={18} className="text-white" />
                </div>
                <a
                  href="tel:+971528401112"
                  dir="ltr"
                  className="text-sm md:text-base hover:underline text-white/90 hover:text-white transition-colors"
                >
                  +971 52 840 1112
                </a>
              </motion.li>
              <motion.li
                whileHover={{ x: 5 }}
                className="flex items-center gap-3 group"
              >
                <div className="p-2 rounded-lg bg-white/10 group-hover:bg-white/20 transition-colors">
                  <MessageSquare size={18} className="text-white" />
                </div>
                <a
                  href="tel:+971528401112"
                  dir="ltr"
                  className="text-sm md:text-base hover:underline text-white/90 hover:text-white transition-colors"
                >
                  +971 52 840 1112
                </a>
              </motion.li>
              <motion.li
                whileHover={{ x: 5 }}
                className="flex items-center gap-3 group"
              >
                <div className="p-2 rounded-lg bg-white/10 group-hover:bg-white/20 transition-colors">
                  <Mail size={18} className="text-white" />
                </div>
                <a
                  href="mailto:info@twhmenu.com"
                  dir="ltr"
                  className="text-sm md:text-base hover:underline text-white/90 hover:text-white transition-colors"
                >
                  info@twhmenu.com
                </a>
              </motion.li>
            </ul>
          </motion.div>

          {/* Quick Links Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-4"
          >
            <h3 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">
              {t("footer.qickLinks")}
            </h3>
            <ul className="space-y-3 md:space-y-4">
              <motion.li whileHover={{ x: 5 }}>
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm md:text-base hover:underline text-white/90 hover:text-white transition-colors block"
                >
                  {t("pages.privacyPolicy")}
                </a>
              </motion.li>
              <motion.li whileHover={{ x: 5 }}>
                <a
                  href="/terms-of-service"
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm md:text-base hover:underline text-white/90 hover:text-white transition-colors block"
                >
                  {t("pages.termsOfService")}
                </a>
              </motion.li>
              <motion.li whileHover={{ x: 5 }}>
                <a
                  href="/services"
                  className="text-sm md:text-base hover:underline text-white/90 hover:text-white transition-colors block"
                >
                  {t("pages.services")}
                </a>
              </motion.li>
            </ul>
            <div className="mt-6 md:mt-8">
              <Select
                onValueChange={handelSelectChange}
                value={lang}
                defaultValue={lang}
              >
                <SelectTrigger className="w-full md:w-[180px] bg-white/10 border-white/20 text-white hover:bg-white/20 transition-colors">
                  <SelectValue className="text-white" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="ar">العربية</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </motion.div>
        </div>

        {/* Social Icons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-10 md:mt-12 pt-8 md:pt-10 border-t border-white/20 flex flex-wrap gap-4 md:gap-6 justify-center md:justify-end"
        >
          <motion.a
            whileHover={{ scale: 1.2, y: -3 }}
            whileTap={{ scale: 0.9 }}
            href="https://www.instagram.com/twh.tech?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw=="
            target="_blank"
            rel="noreferrer"
            className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-300"
          >
            <Instagram size={20} className="text-white" />
          </motion.a>
          <motion.a
            whileHover={{ scale: 1.2, y: -3 }}
            whileTap={{ scale: 0.9 }}
            href="tel:+971528401112"
            dir="ltr"
            target="_blank"
            rel="noreferrer"
            className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-300"
          >
            <PhoneIcon size={20} className="text-white" />
          </motion.a>
          <motion.a
            whileHover={{ scale: 1.2, y: -3 }}
            whileTap={{ scale: 0.9 }}
            dir="ltr"
            target="_blank"
            rel="noreferrer"
            href="https://wa.me///+971528401112?text=Hello%21+I+found+TWHMENU.com+and+I+want+to+create+digital+menu"
            className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-300"
          >
            <FaWhatsapp size={20} className="text-white" />
          </motion.a>
          <motion.a
            whileHover={{ scale: 1.2, y: -3 }}
            whileTap={{ scale: 0.9 }}
            href="mailto:info@twhmenu.com"
            dir="ltr"
            target="_blank"
            rel="noreferrer"
            className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-300"
          >
            <Mail size={20} className="text-white" />
          </motion.a>
        </motion.div>
      </div>
    </footer>
  );
}

export default FooterPages;
